import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, GripVertical, Save, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";
import { toast } from "sonner";

const BLOCK_KINDS = [
  { kind: "heading", label: "Heading", group: "content" },
  { kind: "rich_text", label: "Rich text", group: "content" },
  { kind: "video", label: "Video embed", group: "content" },
  { kind: "image", label: "Image", group: "content" },
  { kind: "link", label: "Link / file", group: "content" },
  { kind: "page_break", label: "Page break", group: "content" },
  { kind: "short_text", label: "Short text", group: "question" },
  { kind: "long_text", label: "Long text", group: "question" },
  { kind: "email", label: "Email", group: "question" },
  { kind: "phone", label: "Phone", group: "question" },
  { kind: "url", label: "URL", group: "question" },
  { kind: "number", label: "Number", group: "question" },
  { kind: "currency", label: "Currency", group: "question" },
  { kind: "single_choice", label: "Single choice", group: "question" },
  { kind: "multi_choice", label: "Multi choice", group: "question" },
  { kind: "dropdown", label: "Dropdown", group: "question" },
  { kind: "yes_no", label: "Yes / No", group: "question" },
  { kind: "rating", label: "Rating", group: "question" },
  { kind: "ranking", label: "Ranking", group: "question" },
  { kind: "matrix", label: "Matrix", group: "question" },
  { kind: "date", label: "Date", group: "question" },
  { kind: "city", label: "City", group: "question" },
  { kind: "file_upload", label: "File upload", group: "question" },
  { kind: "video_answer", label: "Video answer", group: "question" },
  { kind: "audio_answer", label: "Audio answer", group: "question" },
  { kind: "consent", label: "Consent", group: "question" },
  { kind: "calendar_booking", label: "Calendar booking", group: "question" },
];

const emptyBlock = (kind, step) => ({
  block_id: `blk_${Math.random().toString(36).slice(2, 10)}`,
  step,
  kind,
  label: kind === "page_break" ? "" : "Untitled",
  help_text: "",
  required: false,
  options: ["single_choice", "multi_choice", "dropdown", "ranking"].includes(kind) ? ["Option 1", "Option 2"] : [],
  placeholder: "",
  settings: {},
  scoring_weight: 0,
  knockout_rules: [],
  ai_rubric: "",
  show_if: null,
});

export default function FormBuilder() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("New role");
  const [slug, setSlug] = useState("");
  const [role, setRole] = useState("");
  const [blocks, setBlocks] = useState([
    emptyBlock("heading", 1),
    emptyBlock("short_text", 1),
    emptyBlock("email", 1),
  ]);
  const [stepsCount, setStepsCount] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [settings, setSettings] = useState({});
  const [interview, setInterview] = useState({});

  useEffect(() => {
    if (!formId) return;
    (async () => {
      try {
        const r = await api.get(`/forms/${formId}`);
        setTitle(r.data.title);
        setSlug(r.data.slug);
        setRole(r.data.role || "");
        setBlocks(r.data.blocks || []);
        setStepsCount(Math.max(1, ...(r.data.blocks || []).map((b) => b.step || 1)));
        setSettings(r.data.settings || {});
        setInterview(r.data.interview || {});
      } catch { toast.error("Form not found"); }
    })();
  }, [formId]);

  const selected = useMemo(() => blocks.find((b) => b.block_id === selectedId) || null, [blocks, selectedId]);
  const updateSelected = (patch) => {
    setBlocks((bs) => bs.map((b) => (b.block_id === selectedId ? { ...b, ...patch } : b)));
  };

  const addBlock = (kind) => {
    const step = Math.max(1, ...blocks.map((b) => b.step || 1), stepsCount);
    const nb = emptyBlock(kind, step);
    setBlocks([...blocks, nb]);
    setSelectedId(nb.block_id);
  };
  const deleteBlock = (id) => {
    setBlocks((bs) => bs.filter((b) => b.block_id !== id));
    if (selectedId === id) setSelectedId(null);
  };
  const moveBlock = (id, dir) => {
    setBlocks((bs) => {
      const idx = bs.findIndex((b) => b.block_id === id);
      if (idx < 0) return bs;
      const j = idx + dir;
      if (j < 0 || j >= bs.length) return bs;
      const copy = bs.slice();
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
  };
  const setBlockStep = (id, step) => {
    setBlocks((bs) => bs.map((b) => (b.block_id === id ? { ...b, step } : b)));
  };

  const save = async () => {
    if (!title.trim()) return toast.error("Title required");
    const payload = { title, slug: slug || undefined, role, blocks, settings, interview };
    try {
      let r;
      if (formId) r = await api.put(`/forms/${formId}`, payload);
      else r = await api.post("/forms", payload);
      toast.success(formId ? "Saved" : "Form created");
      navigate(`/admin/forms/${r.data.form_id}/edit`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const stepPills = [];
  for (let i = 1; i <= stepsCount; i++) stepPills.push(i);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate("/admin/forms")} className="tt-btn-ghost p-1.5">
            <ArrowLeft size={16} strokeWidth={1.75} />
          </button>
          <div className="min-w-0">
            <input
              data-testid={T.builder.titleInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="tt-input text-lg !py-1.5 w-[380px] max-w-full"
              placeholder="Form title"
            />
            <div className="flex items-center gap-2 mt-1">
              <span className="tt-label">slug</span>
              <input
                data-testid={T.builder.slugInput}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="auto-generated"
                className="bg-transparent text-xs font-mono text-[#5B9BFF] outline-none border-b border-dashed border-[#262626] focus:border-[#5B9BFF] px-1"
              />
              <span className="tt-label pl-4">role</span>
              <input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="e.g. Motion Designer"
                className="bg-transparent text-xs text-[#F4F4F5] outline-none border-b border-dashed border-[#262626] focus:border-[#5B9BFF] px-1"
              />
            </div>
          </div>
        </div>
        <button data-testid={T.builder.saveBtn} onClick={save} className="tt-btn-primary flex items-center gap-2">
          <Save size={14} strokeWidth={1.75} /> Save
        </button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Palette */}
        <aside className="col-span-3 tt-card p-3 h-[calc(100vh-180px)] overflow-auto">
          <div className="tt-label mb-2">Content</div>
          <div className="grid grid-cols-2 gap-1 mb-4">
            {BLOCK_KINDS.filter((b) => b.group === "content").map((b) => (
              <button
                key={b.kind}
                data-testid={T.builder.paletteItem(b.kind)}
                onClick={() => addBlock(b.kind)}
                className="tt-btn-secondary text-xs !py-1.5 text-left"
              >
                {b.label}
              </button>
            ))}
          </div>
          <div className="tt-label mb-2">Questions</div>
          <div className="grid grid-cols-2 gap-1">
            {BLOCK_KINDS.filter((b) => b.group === "question").map((b) => (
              <button
                key={b.kind}
                data-testid={T.builder.paletteItem(b.kind)}
                onClick={() => addBlock(b.kind)}
                className="tt-btn-secondary text-xs !py-1.5 text-left"
              >
                {b.label}
              </button>
            ))}
          </div>
        </aside>

        {/* Canvas */}
        <section className="col-span-6 tt-card p-5 h-[calc(100vh-180px)] overflow-auto">
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            {stepPills.map((n) => (
              <span
                key={n}
                className="font-mono text-xs px-2.5 py-1 rounded border border-[#262626] bg-[#1A1A1A] text-[#A1A1AA]"
              >
                Step {n}
              </span>
            ))}
            <button
              data-testid={T.builder.addStep}
              onClick={() => setStepsCount((n) => n + 1)}
              className="tt-btn-ghost text-xs flex items-center gap-1"
            >
              <Plus size={12} /> Add step
            </button>
          </div>

          {blocks.length === 0 && (
            <div className="text-sm text-[#71717A] py-10 text-center">
              Add blocks from the palette on the left.
            </div>
          )}
          <div className="space-y-2">
            {blocks.map((b, i) => (
              <div
                key={b.block_id}
                data-testid={T.builder.blockRow(b.block_id)}
                onClick={() => setSelectedId(b.block_id)}
                className={`flex items-start gap-2 p-3 rounded-md border cursor-pointer transition-colors ${
                  selectedId === b.block_id
                    ? "border-[#5B9BFF] bg-[rgba(91,155,255,0.06)]"
                    : "border-[#262626] bg-[#0F0F0F] hover:border-[#3F3F46]"
                }`}
              >
                <div className="flex flex-col items-center text-[#71717A]">
                  <button onClick={(e) => { e.stopPropagation(); moveBlock(b.block_id, -1); }} className="hover:text-[#F4F4F5]">↑</button>
                  <GripVertical size={14} />
                  <button onClick={(e) => { e.stopPropagation(); moveBlock(b.block_id, 1); }} className="hover:text-[#F4F4F5]">↓</button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="tt-badge-info">{b.kind}</span>
                    <span className="tt-badge-neutral">Step {b.step}</span>
                    {b.required && <span className="tt-badge-warn">required</span>}
                    {b.scoring_weight > 0 && <span className="tt-badge-pass">w {b.scoring_weight}</span>}
                    {b.knockout_rules?.length > 0 && <span className="tt-badge-danger">knockout</span>}
                  </div>
                  <div className="mt-1 text-sm">{b.label || <span className="text-[#71717A]">(no label)</span>}</div>
                  {b.help_text && <div className="text-xs text-[#71717A] mt-0.5">{b.help_text}</div>}
                </div>
                <button
                  data-testid={T.builder.deleteBlock(b.block_id)}
                  onClick={(e) => { e.stopPropagation(); deleteBlock(b.block_id); }}
                  className="tt-btn-ghost p-1 text-[#71717A] hover:text-[#EF4444]"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Inspector */}
        <aside className="col-span-3 tt-card p-4 h-[calc(100vh-180px)] overflow-auto">
          {!selected && <div className="text-sm text-[#71717A]">Select a block to edit.</div>}
          {selected && (
            <div className="space-y-4">
              <div>
                <div className="tt-label mb-1">Label</div>
                <input
                  data-testid={T.builder.insLabel}
                  value={selected.label || ""}
                  onChange={(e) => updateSelected({ label: e.target.value })}
                  className="tt-input"
                />
              </div>
              <div>
                <div className="tt-label mb-1">Help text</div>
                <input
                  value={selected.help_text || ""}
                  onChange={(e) => updateSelected({ help_text: e.target.value })}
                  className="tt-input"
                />
              </div>
              <div>
                <div className="tt-label mb-1">Step</div>
                <input
                  type="number"
                  min={1}
                  value={selected.step}
                  onChange={(e) => setBlockStep(selected.block_id, Math.max(1, parseInt(e.target.value || "1")))}
                  className="tt-input font-mono"
                />
              </div>
              <label className="flex items-center justify-between py-1">
                <span className="text-sm text-[#A1A1AA]">Required</span>
                <input
                  type="checkbox"
                  data-testid={T.builder.insRequired}
                  checked={!!selected.required}
                  onChange={(e) => updateSelected({ required: e.target.checked })}
                  className="accent-[#5B9BFF]"
                />
              </label>
              {["single_choice", "multi_choice", "dropdown", "ranking"].includes(selected.kind) && (
                <div>
                  <div className="tt-label mb-1">Options (one per line)</div>
                  <textarea
                    data-testid={T.builder.insOptions}
                    rows={5}
                    value={(selected.options || []).join("\n")}
                    onChange={(e) => updateSelected({ options: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
                    className="tt-input font-mono text-xs"
                  />
                </div>
              )}
              <div>
                <div className="tt-label mb-1">Scoring weight</div>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  data-testid={T.builder.insWeight}
                  value={selected.scoring_weight || 0}
                  onChange={(e) => updateSelected({ scoring_weight: parseFloat(e.target.value || "0") })}
                  className="tt-input font-mono"
                />
              </div>
              <div>
                <div className="tt-label mb-1">AI rubric</div>
                <textarea
                  rows={3}
                  data-testid={T.builder.insRubric}
                  value={selected.ai_rubric || ""}
                  onChange={(e) => updateSelected({ ai_rubric: e.target.value })}
                  className="tt-input text-sm"
                  placeholder="How Claude should score an open answer…"
                />
              </div>
              <div>
                <div className="tt-label mb-1">Knockout rule</div>
                <div className="flex gap-1 flex-wrap">
                  <select
                    data-testid={T.builder.insKnockout}
                    value={selected.knockout_rules?.[0]?.op || ""}
                    onChange={(e) => {
                      const op = e.target.value;
                      if (!op) return updateSelected({ knockout_rules: [] });
                      const rules = [{ op, value: selected.knockout_rules?.[0]?.value || "" }];
                      updateSelected({ knockout_rules: rules });
                    }}
                    className="tt-input !w-auto"
                  >
                    <option value="">— none —</option>
                    <option value="gt">greater than</option>
                    <option value="lt">less than</option>
                    <option value="eq">equals</option>
                    <option value="neq">not equals</option>
                    <option value="contains">contains</option>
                  </select>
                  {selected.knockout_rules?.length > 0 && (
                    <input
                      className="tt-input !w-auto flex-1"
                      value={selected.knockout_rules[0].value}
                      onChange={(e) => updateSelected({
                        knockout_rules: [{ op: selected.knockout_rules[0].op, value: e.target.value }],
                      })}
                      placeholder="value"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
